/**
 * Påminnelser för kundtoppnod: besiktning och fordonsskatt.
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import type { ReminderServiceSettings, ReminderSettings } from '@/types/organizations';
import { DEFAULT_REMINDER_SETTINGS, mergeReminderOrgSettings } from '@/utils/organizationReminderDefaults';

interface Props {
  value: ReminderSettings | undefined | null;
  onChange: (next: ReminderSettings) => void;
}

function YesNo({
  value,
  onChange,
  yesLabel,
  noLabel,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  yesLabel: string;
  noLabel: string;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 py-1 text-sm ${value ? 'bg-bgPrimary text-white' : 'bg-bgContainer text-textSecondary hover:text-textDefault'}`}
      >
        {yesLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 py-1 text-sm border-l border-border ${!value ? 'bg-bgPrimary text-white' : 'bg-bgContainer text-textSecondary hover:text-textDefault'}`}
      >
        {noLabel}
      </button>
    </div>
  );
}

const OrganizationReminderSettings: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const s = mergeReminderOrgSettings(value);

  const patchService = (key: keyof ReminderSettings, next: Partial<ReminderServiceSettings>) => {
    const current = s[key];
    const merged = { ...current, ...next };
    if (!merged.enabled) merged.notify = false;
    onChange({
      ...DEFAULT_REMINDER_SETTINGS,
      ...s,
      [key]: merged,
    });
  };

  const row = (key: keyof ReminderSettings, title: string, help: string) => {
    const svc = s[key];
    return (
      <div className="p-4 rounded-lg border border-border bg-bgContainer space-y-3">
        <div>
          <h4 className="text-sm font-medium text-textDefault">{title}</h4>
          <p className="text-xs text-textSecondary mt-0.5">{help}</p>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-textDefault">
            <span className="font-medium w-28">{t('organizations.reminders.service')}</span>
            <YesNo
              value={svc.enabled}
              onChange={(enabled) => patchService(key, { enabled })}
              yesLabel={t('common.yes')}
              noLabel={t('common.no')}
            />
          </label>
          <label className={`flex items-center gap-2 text-sm text-textDefault ${svc.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <span className="font-medium w-28">{t('organizations.reminders.notify')}</span>
            <YesNo
              value={svc.notify}
              onChange={(notify) => patchService(key, { notify })}
              yesLabel={t('common.yes')}
              noLabel={t('common.no')}
            />
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4 rounded-lg border border-border bg-bgElevated">
      <div>
        <h3 className="text-base font-semibold text-textDefault mb-1">{t('organizations.reminders.title')}</h3>
        <p className="text-sm text-textSecondary">{t('organizations.reminders.intro')}</p>
      </div>
      {row('inspection', t('organizations.reminders.inspection'), t('organizations.reminders.inspectionHelp'))}
      {row('tax', t('organizations.reminders.tax'), t('organizations.reminders.taxHelp'))}
    </div>
  );
};

export default OrganizationReminderSettings;
